/**
 * Quantités de la liste de courses — « 400 g », « 50 cl », « 3 ».
 *
 * Partagé entre le client et le serveur, pour la même raison que
 * `search.ts` : la fusion de deux ajouts tourne des deux côtés (pli
 * optimiste sur le téléphone, application réelle sur le serveur), et deux
 * implémentations qui divergent donnent une ligne dont la quantité change
 * toute seule au rechargement.
 *
 * C'est du TEXTE, pas un couple (nombre, unité) en base. Les recettes
 * stockent déjà `quantityText`, et une quantité d'achat est trop variée pour
 * un schéma fermé (« 1 botte », « 2 barquettes », « un peu »). On ne
 * structure qu'au moment de calculer, et seulement ce qu'on sait calculer.
 */

export const MAX_QUANTITE = 40

const NOMBRE = String.raw`\d+(?:[.,]\d+)?`
const UNITE = String.raw`(?:kilos?|kg|grammes?|g|mg|litres?|l|cl|ml|dl)`

const CANONIQUE: Record<string, string> = {
  kilo: 'kg', kilos: 'kg', kg: 'kg',
  gramme: 'g', grammes: 'g', g: 'g',
  mg: 'mg',
  litre: 'l', litres: 'l', l: 'l',
  cl: 'cl', ml: 'ml', dl: 'dl',
}

function unite(brut: string): string {
  const u = brut.trim().replace(/\.$/, '')
  return CANONIQUE[u.toLowerCase()] ?? u
}

/**
 * « 400g » → « 400 g », « 1L » → « 1 l », « 2 » → « 2 ». Vide → `null`.
 *
 * On ne touche qu'à ce qui commence par un nombre suivi d'un mot : « 2x200g »
 * ou « une pincée » sont gardés tels quels plutôt que mal devinés.
 */
export function normaliseQuantite(brut: unknown): string | null {
  const s = String(brut ?? '').replace(/\s+/g, ' ').trim()
  if (!s) return null
  const m = new RegExp(String.raw`^(${NOMBRE})\s*(\p{L}[\p{L}.' ]*)?$`, 'u').exec(s)
  const net = m ? `${m[1]} ${m[2] ? unite(m[2]) : ''}`.trim() : s
  return net.slice(0, MAX_QUANTITE)
}

/**
 * Sépare « 400g de farine », « farine 400 g » ou « 2 poivrons » en un nom et
 * une quantité. Sert au champ de saisie rapide : on tape comme on parle.
 *
 * Volontairement étroit. Un nombre seul n'est pris pour une quantité que
 * s'il tient en deux chiffres : « 1664 blonde » est une bière, pas 1664
 * blondes. Et une unité seule en queue (« farine 400 g ») ne compte que si
 * elle est connue, sinon « Haribo 2 boîtes » perdrait son nom.
 */
export function separerQuantite(saisie: string): { nom: string; quantite: string | null } {
  const s = saisie.replace(/\s+/g, ' ').trim()

  const debutUnite = new RegExp(
    String.raw`^(${NOMBRE})\s*(${UNITE})\b\.?\s+(?:de\s+|d['’]\s*)?(.+)$`,
    'iu',
  ).exec(s)
  if (debutUnite) {
    return { nom: debutUnite[3]!, quantite: normaliseQuantite(`${debutUnite[1]} ${debutUnite[2]}`) }
  }

  const finUnite = new RegExp(String.raw`^(.+?)\s+(${NOMBRE})\s*(${UNITE})$`, 'iu').exec(s)
  if (finUnite) {
    return { nom: finUnite[1]!, quantite: normaliseQuantite(`${finUnite[2]} ${finUnite[3]}`) }
  }

  const debutNombre = /^(\d{1,2})\s+(?:de\s+|d['’]\s*)?(.+)$/iu.exec(s)
  if (debutNombre && !new RegExp(`^${UNITE}$`, 'iu').test(debutNombre[2]!)) {
    return { nom: debutNombre[2]!, quantite: debutNombre[1]! }
  }

  return { nom: s, quantite: null }
}

function nombre(s: string): number {
  return Number(s.replace(',', '.'))
}

function formate(n: number): string {
  return String(Math.round(n * 1000) / 1000).replace('.', ',')
}

/**
 * Ce que devient la quantité d'une ligne quand on y ajoute encore.
 *
 *  - Même unité (ou aucune) : on ADDITIONNE. « 2 » puis « 3 » donne « 5 »,
 *    « 250 g » puis « 100 g » donne « 350 g ». Les deux recettes de la
 *    semaine qui demandent des oignons doivent faire acheter la somme.
 *  - Unités différentes ou texte libre : on JUXTAPOSE (« 500 g + 1 pot »).
 *    Convertir des grammes en pots serait inventer ; garder les deux est
 *    honnête, et on corrige d'un tap sur la quantité.
 *
 * Additionner même deux quantités identiques est un choix. L'erreur
 * inverse — deux recettes à « 2 oignons » qui n'en font acheter que deux —
 * se découvre en cuisinant ; un doublon d'ajout se voit sur la liste.
 */
export function fusionnerQuantites(actuelle: unknown, ajout: unknown): string | null {
  const a = normaliseQuantite(actuelle)
  const b = normaliseQuantite(ajout)
  if (!a) return b
  if (!b) return a

  const re = new RegExp(String.raw`^(${NOMBRE})(?: (.+))?$`, 'u')
  const ma = re.exec(a)
  const mb = re.exec(b)
  if (ma && mb && (ma[2] ?? '').toLowerCase() === (mb[2] ?? '').toLowerCase()) {
    return `${formate(nombre(ma[1]!) + nombre(mb[1]!))}${ma[2] ? ` ${ma[2]}` : ''}`
  }
  return `${a} + ${b}`
}
